import React, { useEffect, useState } from "react";
import {
  adminSearchUsers,
  adminPromoteTier,
  adminRevokeTier,
} from "../../lib/api";

/**
 * UsersSection — admin home for the tier-promotion feature.
 *
 * Operator workflow
 * -----------------
 *   1. Search by name / email / user_id.
 *   2. Click PROMOTE on a row → opens the modal.
 *   3. Pick tier (PRO / ELITE) + duration (7 / 30 / 90 / 365 days / LIFETIME)
 *      and an optional reason.
 *   4. Confirm. An inbox notif fires to the user; the audit log records
 *      the action.
 *
 * The promotion is purely additive — paid Stripe subs are unaffected
 * (the merge in `resolve_effective_tier` only upgrades, never
 * downgrades). Expired promotions evaporate automatically on next
 * read — no cron job needed.
 */
export function UsersSection({ onToast }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // user row being promoted

  const search = React.useCallback(async (query) => {
    setLoading(true);
    try {
      const data = await adminSearchUsers(query, 25);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Search failed.");
    } finally { setLoading(false); }
  }, [onToast]);

  useEffect(() => { search(""); }, [search]);

  const revoke = async (uid) => {
    if (!window.confirm("Revoke this user's tier promotion?")) return;
    try {
      await adminRevokeTier(uid);
      onToast?.("Promotion revoked.");
      search(q);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Revoke failed.");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-section">
      <div className="rounded-2xl p-4 border border-white/10 bg-black/30 flex gap-2">
        <input
          type="text"
          placeholder="Search by name, email or user_id…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search(q)}
          data-testid="admin-users-search-input"
          className="flex-1 bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
        />
        <button
          type="button"
          onClick={() => search(q)}
          data-testid="admin-users-search-btn"
          className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon"
        >
          {loading ? "…" : "SEARCH"}
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-[12px] text-white/40 p-4">No users.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((u) => {
            const promo = u.tier_promotion || null;
            const expIso = promo?.expires_at;
            const expiry = expIso ? new Date(expIso) : null;
            const active = promo && (!expiry || expiry > new Date());
            return (
              <li
                key={u.user_id}
                data-testid={`admin-user-row-${u.user_id}`}
                className="p-3 rounded-xl bg-black/40 border border-white/10 flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-sm truncate">
                    {u.name || u.user_id}
                    <span className="ml-2 text-[10px] tracking-widest text-white/40 uppercase">
                      {u.user_type || "casual"}
                    </span>
                    {active && (
                      <span className="ml-2 text-[10px] tracking-widest text-[#14b8a6] font-bold">
                        · PROMOTED {promo.tier?.toUpperCase()}
                        {expiry ? ` UNTIL ${expiry.toISOString().slice(0, 10)}` : " · LIFETIME"}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-white/40 truncate">
                    {u.email} · <code className="text-[10px]">{u.user_id}</code>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(u)}
                  data-testid={`admin-user-promote-${u.user_id}`}
                  className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/15 border border-[#14b8a6]/40 text-[#14b8a6]"
                >
                  PROMOTE
                </button>
                {active && (
                  <button
                    type="button"
                    onClick={() => revoke(u.user_id)}
                    data-testid={`admin-user-revoke-${u.user_id}`}
                    className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/50 text-red-200"
                  >
                    REVOKE
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {editing && (
        <PromoteTierModal
          user={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); search(q); }}
          onToast={onToast}
        />
      )}
    </div>
  );
}

const DURATIONS = [
  { label: "7 DAYS",   days: 7 },
  { label: "30 DAYS",  days: 30 },
  { label: "90 DAYS",  days: 90 },
  { label: "1 YEAR",   days: 365 },
  { label: "LIFETIME", days: null },
];

function PromoteTierModal({ user, onClose, onDone, onToast }) {
  const [tier, setTier] = useState("pro");
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await adminPromoteTier(user.user_id, tier, days, reason);
      const lbl = days ? `for ${days} days` : "permanently";
      onToast?.(`✅ ${user.name || user.user_id} → ${tier.toUpperCase()} ${lbl}`);
      onDone?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Promote failed.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="admin-promote-modal"
    >
      <div className="w-full max-w-md rounded-2xl border border-[#14b8a6]/40 bg-[#070c0b]">
        <header className="p-4 flex items-center justify-between border-b border-white/5">
          <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
            <i className="fas fa-crown mr-1" />PROMOTE TIER
          </p>
          <button type="button" onClick={onClose}
                  className="text-white/50 hover:text-white text-lg">×</button>
        </header>
        <div className="p-4 space-y-3">
          <p className="text-[12px] text-white/70">
            Granting promotion to <b className="text-white">{user.name || user.user_id}</b>
          </p>

          <div>
            <p className="text-[10px] tracking-[3px] text-white/50 mb-1.5">TIER</p>
            <div className="flex gap-2">
              {["pro", "elite"].map((t) => (
                <button key={t}
                        type="button"
                        onClick={() => setTier(t)}
                        data-testid={`promote-tier-${t}`}
                        className={`flex-1 px-3 py-2 rounded-xl text-[11px] tracking-widest font-bold border ${
                          tier === t
                            ? "bg-[#14b8a6]/20 border-[#14b8a6] text-[#14b8a6]"
                            : "bg-black/30 border-white/10 text-white/60"
                        }`}>
                  {t.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] tracking-[3px] text-white/50 mb-1.5">DURATION</p>
            <div className="grid grid-cols-3 gap-2">
              {DURATIONS.map((d) => (
                <button key={d.label}
                        type="button"
                        onClick={() => setDays(d.days)}
                        data-testid={`promote-duration-${d.days || "lifetime"}`}
                        className={`px-2 py-2 rounded-xl text-[10px] tracking-widest font-bold border ${
                          days === d.days
                            ? "bg-[#14b8a6]/20 border-[#14b8a6] text-[#14b8a6]"
                            : "bg-black/30 border-white/10 text-white/60"
                        }`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[10px] tracking-[3px] text-white/50 mb-1.5">REASON (optional)</p>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. content creator, sponsor comp, beta tester…"
              data-testid="promote-reason-input"
              maxLength={200}
              className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
            />
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="promote-submit-btn"
            className="w-full rounded-xl bg-gradient-to-r from-[#14b8a6] to-[#0d9488] text-black text-xs tracking-widest font-bold py-2.5 hover:brightness-110 disabled:opacity-50"
          >
            {busy ? "…" : `GRANT ${tier.toUpperCase()} ${days ? `· ${days} DAYS` : "· LIFETIME"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
